import { RunRatioConfig } from "../agent-config-options";

/**
 * Default dispatch-to-housekeeping ratio — openhi's `DISPATCHER.md`
 * ships a 4:1 ratio: four consecutive dispatch runs, then one
 * housekeeping run, then the counter wraps. The ratio value stored
 * here is the dispatch-run count per housekeeping run; with
 * `ratio = 4`, runs 1–4 dispatch and run 5 housekeeps. The cycle
 * length is therefore `ratio + 1`.
 *
 * @see RunRatioConfig
 */
export const DEFAULT_DISPATCH_TO_HOUSEKEEPING_RATIO = 4;

/**
 * Default on-disk path for the orchestrator run-counter state file,
 * relative to the repo root. The file is tiny JSON
 * (`{ "run_counter": <n> }`) and is gitignored in most consumer repos
 * because it's local-only — each operator's orchestrator session
 * maintains its own counter.
 *
 * @see RunRatioConfig
 */
export const DEFAULT_STATE_FILE_PATH = ".state/orchestrator-runs.json";

/**
 * Default recommended model label for dispatch runs. Rendered into
 * the orchestrator-conventions rule so agents and humans can read the
 * model pairing at a glance; the string is purely informational and
 * does not cause configulator to set `AGENT_MODEL` on the sub-agent.
 *
 * @see RunRatioConfig
 */
export const DEFAULT_DISPATCH_MODEL = "opus";

/**
 * Default recommended model label for housekeeping runs. See
 * `DEFAULT_DISPATCH_MODEL` for the rendering contract. Housekeeping
 * runs are mechanical (batch PR review + maintenance scan), so a
 * cheaper model like Sonnet is the documented recommendation.
 *
 * @see RunRatioConfig
 */
export const DEFAULT_HOUSEKEEPING_MODEL = "sonnet";

/**
 * Fully-resolved run-ratio settings. Every field is defaulted so
 * downstream renderers can reason about a single canonical shape.
 */
export interface ResolvedRunRatio {
  readonly enabled: boolean;
  readonly ratio: number;
  readonly stateFilePath: string;
  readonly dispatchModel: string;
  readonly housekeepingModel: string;
}

/**
 * Resolve a (possibly absent) `RunRatioConfig` into a canonical
 * `ResolvedRunRatio` with every field filled in. Unset fields
 * cascade from their documented defaults.
 *
 * Malformed configs (non-integer or non-positive ratio, empty or
 * whitespace-only state file path) throw a descriptive `Error` —
 * callers should not need to guard against it at runtime.
 */
export function resolveRunRatio(config?: RunRatioConfig): ResolvedRunRatio {
  const ratio = config?.ratio ?? DEFAULT_DISPATCH_TO_HOUSEKEEPING_RATIO;
  assertValidRatio(ratio);

  const stateFilePath = config?.stateFilePath ?? DEFAULT_STATE_FILE_PATH;
  assertValidStateFilePath(stateFilePath);

  return {
    enabled: config?.enabled ?? true,
    ratio,
    stateFilePath,
    dispatchModel: config?.dispatchModel ?? DEFAULT_DISPATCH_MODEL,
    housekeepingModel: config?.housekeepingModel ?? DEFAULT_HOUSEKEEPING_MODEL,
  };
}

/**
 * Synth-time validation hook. Throws a descriptive `Error` when the
 * supplied `RunRatioConfig` is malformed. Called by
 * `AgentConfig.preSynthesize` before any rendering so a misconfigured
 * ratio fails the build instead of silently shipping broken
 * housekeeping cadence. Returns the resolved ratio unchanged so
 * callers can write `const rr = validateRunRatioConfig(config)` in
 * one line.
 *
 * Malformed cases rejected here:
 *
 * - `ratio` non-integer, zero, or negative.
 * - `stateFilePath` empty / whitespace-only, or an absolute path
 *   (the state file must live inside the repo).
 */
export function validateRunRatioConfig(
  config?: RunRatioConfig,
): ResolvedRunRatio {
  return resolveRunRatio(config);
}

/**
 * Compute the run type (`"dispatch"` or `"housekeeping"`) for a
 * given run counter value and resolved ratio. Used by the TypeScript
 * side (tests, downstream consumers that want to reason about the
 * cadence without shelling out). The shell helper produced by
 * `renderRunRatioShellHelpers` implements the same logic.
 *
 * Every `(ratio + 1)`th run is a housekeeping run; all others
 * dispatch. For `ratio = 4`, runs 1–4 dispatch and run 5
 * housekeeps; run 6 is again dispatch; run 10 housekeeps.
 */
export function classifyRun(
  runCounter: number,
  ratio: ResolvedRunRatio,
): "dispatch" | "housekeeping" {
  if (!ratio.enabled) {
    return "dispatch";
  }
  const cycle = ratio.ratio + 1;
  return runCounter > 0 && runCounter % cycle === 0
    ? "housekeeping"
    : "dispatch";
}

/**
 * Render the markdown subsection appended to the
 * `orchestrator-conventions` rule. Always returns a non-empty string
 * so the orchestrator rule documents the cadence even when the
 * consumer relies on the defaults.
 */
export function renderRunRatioSection(ratio: ResolvedRunRatio): string {
  const lines: Array<string> = [
    "## Run ratio (dispatch vs housekeeping)",
    "",
    "The orchestrator keeps a **persistent run counter** and interleaves",
    "dispatch runs (pick the next ready issue, recommend a worker) with",
    "**housekeeping runs** (batch PR review + maintenance scan) on a",
    "configurable ratio. This mirrors openhi's `DISPATCHER.md` contract:",
    "multiple dispatch runs feed the worker queue, then one batched",
    "housekeeping run flushes review backlog and runs maintenance",
    "triage so the pipeline never drifts.",
    "",
  ];

  if (!ratio.enabled) {
    lines.push(
      "**The run ratio is disabled for this project.** Every orchestrator",
      "run executes the full dispatch pipeline; PR review and maintenance",
      "remain manual invocations. Enable the ratio via",
      "`AgentConfigOptions.runRatio.enabled = true` once the operator is",
      "comfortable with the counter-backed cadence.",
      "",
    );
    return lines.join("\n");
  }

  const cycle = ratio.ratio + 1;

  lines.push(
    "### Cadence",
    "",
    `The cycle length is **${cycle}** runs:`,
    "",
    `- Runs 1 through ${ratio.ratio} execute the **dispatch** pipeline`,
    `  (recommended model: \`${ratio.dispatchModel}\`).`,
    `- Run ${cycle} executes the **housekeeping** pipeline`,
    `  (recommended model: \`${ratio.housekeepingModel}\`).`,
    `- The counter wraps — run ${cycle + 1} is a dispatch run again, run ${cycle * 2} is the next housekeeping run, and so on.`,
    "",
    "The orchestrator increments the counter **once per invocation** at",
    "the top of the run, before any pipeline phase executes. The",
    "pre-increment value is never observed — the tick always returns",
    "the post-increment counter and the classified run type in a single",
    "atomic update.",
    "",
    "### State file",
    "",
    `The run counter persists at \`${ratio.stateFilePath}\`. The file is`,
    "plain JSON with a single `run_counter` integer field:",
    "",
    "```json",
    '{ "run_counter": 42 }',
    "```",
    "",
    "The state file is **gitignored** in consumer repos (it is local to",
    "each operator's machine). On a first run, or if the file is missing",
    "or corrupt, the orchestrator recreates it with `run_counter: 1`.",
    "",
    "### Dispatch-run pipeline",
    "",
    "1. Phase A — startup (fetch + checkout default branch).",
    "2. Phase C — triage unblock (resolve `Depends on:` chains).",
    "3. Phase E — queue scan (pick the top `PICK` line, run the scope",
    "   gate, emit `NEXT_WORK_ITEM`).",
    "4. Phase F — cleanup.",
    "",
    "### Housekeeping-run pipeline",
    "",
    "1. Phase A — startup.",
    "2. Phase B — batch PR review across every eligible open PR.",
    "3. Phase D — maintenance scan (stale detection, orphaned branches,",
    "   needs-attention summary).",
    "4. Phase F — cleanup.",
    "",
    "### Model recommendations",
    "",
    `Dispatch runs should use \`${ratio.dispatchModel}\` — the routing`,
    "logic, scope gate, and funnel-tier sort benefit from the stronger",
    "reasoning model. Housekeeping runs are mechanical (read CI status,",
    "toggle labels, post canned comments) and should use",
    `\`${ratio.housekeepingModel}\` so the batched PR review and`,
    "maintenance scan cost less per invocation.",
    "",
    "These strings are **informational** — they surface in the",
    "orchestrator's rendered rule content so operators know which model",
    "to run each session against. Configulator does not inject them as",
    "`model:` frontmatter on the sub-agent definition; the operator (or",
    "a scheduled task) picks the model at invocation time.",
  );

  return lines.join("\n");
}

/**
 * Render a shell-script snippet embedded in `check-blocked.sh`. The
 * snippet declares a `run_counter_tick()` function that:
 *
 * 1. Reads the state file (creating it with `run_counter: 1` if
 *    missing or unparseable).
 * 2. Increments the counter.
 * 3. Writes the new counter back atomically (temp file + `mv`).
 * 4. Echoes the post-increment counter and the classified run type
 *    (`dispatch` or `housekeeping`) in the canonical
 *    `run=<n> type=<dispatch|housekeeping>` format.
 *
 * Returns the body of a shell function block (including the
 * `run_counter_tick()` wrapper) so the surrounding script can splice
 * it inline at the exact indent level it wants.
 */
export function renderRunRatioShellHelpers(ratio: ResolvedRunRatio): string {
  const cycle = ratio.ratio + 1;
  return [
    "# Increment the orchestrator run counter and classify the run.",
    "# Reads the state file (creating it on first run or corruption),",
    "# increments the counter, writes back atomically, and echoes",
    "# `run=<n> type=<dispatch|housekeeping>` on stdout.",
    "#",
    "# Uses the cycle length (ratio + 1) hard-coded from the resolved",
    "# RunRatioConfig so the shell helper matches the rendered rule",
    "# content byte-for-byte.",
    "run_counter_tick() {",
    '  local state_file="$ORCHESTRATOR_STATE_FILE"',
    "  local state_dir",
    '  state_dir=$(dirname "$state_file")',
    '  mkdir -p "$state_dir" 2>/dev/null || true',
    "",
    "  local current=0",
    '  if [ -f "$state_file" ]; then',
    "    # jq returns empty string on parse failure; guard against it.",
    "    current=$(jq -r '.run_counter // 0' \"$state_file\" 2>/dev/null || echo 0)",
    '    case "$current" in',
    "      ''|*[!0-9]*) current=0 ;;",
    "    esac",
    "  fi",
    "",
    "  local next=$((current + 1))",
    "",
    '  local tmp_file="${state_file}.tmp.$$"',
    '  printf \'{ "run_counter": %d }\\n\' "$next" > "$tmp_file"',
    '  mv "$tmp_file" "$state_file"',
    "",
    "  local run_type=dispatch",
    `  if [ $((next % ${cycle})) -eq 0 ]; then`,
    "    run_type=housekeeping",
    "  fi",
    '  printf \'run=%d type=%s\\n\' "$next" "$run_type"',
    "}",
  ].join("\n");
}

/*******************************************************************************
 *
 * Internal helpers
 *
 ******************************************************************************/

function assertValidRatio(ratio: number): void {
  if (!Number.isInteger(ratio)) {
    throw new Error(
      `RunRatioConfig.ratio must be a positive integer; got ${ratio}`,
    );
  }
  if (ratio < 1) {
    throw new Error(
      `RunRatioConfig.ratio must be a positive integer; got ${ratio}`,
    );
  }
}

function assertValidStateFilePath(stateFilePath: string): void {
  const trimmed = stateFilePath.trim();
  if (trimmed.length === 0) {
    throw new Error(
      "RunRatioConfig.stateFilePath must be a non-empty string relative to the repo root",
    );
  }
  if (trimmed.startsWith("/")) {
    throw new Error(
      `RunRatioConfig.stateFilePath must be relative to the repo root (no leading '/'); got ${stateFilePath}`,
    );
  }
}
